import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { Switch } from '../../ui/Switch/Switch';
import { TrashBinIcon } from '../../../assets/icons/icons';
import type { PositionDraft } from '../../../types/project';
import { UNITS } from '../../../data/projectCard';
import { useRegions } from '../../../hooks/useRegions';
import { cx } from '../../../utils/cx';
import { MAX_QTY } from '../../../data/projectCard';
import styles from './GoodCard.module.scss';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { Select } from '../../ui/Select/Select';
import {
    formatQty,
    parseQty,
    sanitizeQty
} from '../../../utils/qtyInput';
import {
    caretAfter,
    countBeforeCaret,
    digitsOf,
    groupInt,
    groupPrice,
    stripSpaces
} from '../../../utils/priceInput';
import {
    formatRequestPrice,
    parseRequestPrice,
    sanitizeRequestPriceInput
} from '../../../utils/requestPrice';

interface GoodCardProps {
    index: number;
    draft: PositionDraft;
    invalid: boolean;
    onChange: (patch: Partial<PositionDraft>) => void;
    onRemove: () => void;
    autoFocus?: boolean;
    projectRegion?: string;
    showDimensions?: boolean;
}

export const GoodCard: FC<GoodCardProps> = ({ index, draft, invalid, onChange, onRemove, autoFocus, projectRegion = '', showDimensions = false, }) => {
    const regions = useRegions(draft.region || projectRegion);

    const unitOptions = useMemo(() => UNITS.map((unit) => ({ value: unit, label: unit })), []);
    const regionOptions = useMemo(() => regions.map((region) => ({ value: region, label: region })), [regions]);

    // const allowFraction = draft.unit === 'кг' || draft.unit === 'л';
    const allowFraction = false;
    const [qtyText, setQtyText] = useState(() => formatQty(draft.qty));
    const [priceText, setPriceText] = useState(() => groupPrice(formatRequestPrice(draft.requestPrice)));
    const priceRef = useRef<HTMLInputElement>(null);
    const priceCaret = useRef<number | null>(null);

    useEffect(() => {
        if (parseRequestPrice(stripSpaces(priceText)) !== (draft.requestPrice ?? null)) {
            setPriceText(groupPrice(formatRequestPrice(draft.requestPrice)));
        }
    }, [draft.requestPrice]);

    useLayoutEffect(() => {
        if (priceCaret.current != null && priceRef.current) {
            priceRef.current.setSelectionRange(priceCaret.current, priceCaret.current);
            priceCaret.current = null;
        }
    });

    useEffect(() => {
        if (parseQty(qtyText) !== draft.qty) setQtyText(formatQty(draft.qty));
    }, [draft.qty]);

    const grouped = (value: number): string =>
        value ? value.toLocaleString('ru-RU') : '';

    const digits = (value: string): number =>
        Number(value.replace(/\D/g, '')) || 0;

    const restoreCaret = (input: HTMLInputElement, digitsBefore: number) => {
        if (!digitsBefore) {
            input.setSelectionRange(0, 0);
            return;
        }

        let seen = 0;
        let pos = input.value.length;

        for (let i = 0; i < input.value.length; i += 1) {
            if (/\d/.test(input.value[i])) {
                seen += 1;
                if (seen === digitsBefore) {
                    pos = i + 1;
                    break;
                }
            }
        }

        input.setSelectionRange(pos, pos);
    };

    return (
        <div className={styles.good}>
            <div className={styles.top}>
                <span className={styles.tag}>Товар {index}</span>
                <Tooltip text="Удалить" align="right">
                    <button
                        type="button"
                        className={`${styles.iconBtn} ${styles.danger}`}
                        aria-label="Убрать товар"
                        onClick={onRemove}
                    >
                        <TrashBinIcon className={styles.icon} />
                    </button>
                </Tooltip>
            </div>

            <div className={styles.grid}>
                <div className={styles.rowTop}>
                    <div>
                        <label className={styles.label}>
                            Наименование товара <span className={styles.req}>*</span>
                        </label>
                        <input
                            className={cx(styles.input, invalid && styles.error)}
                            placeholder="Например: лебёдка электрическая 12В"
                            autoFocus={autoFocus}
                            value={draft.name}
                            onChange={(event) => onChange({ name: event.target.value })}
                        />
                    </div>
                    <div>
                        <label className={styles.label}>Дополнительные характеристики</label>
                        <input
                            className={styles.input}
                            placeholder="Материал, размеры, мощность, цвет…"
                            value={draft.spec}
                            onChange={(event) => onChange({ spec: event.target.value })}
                        />
                    </div>
                </div>

                <div className={styles.params}>
                    <div>
                        <label className={styles.label}>Кол-во</label>
                        <input
                            className={styles.input}
                            inputMode={allowFraction ? 'decimal' : 'numeric'}
                            value={qtyText}
                            onChange={(event) => {
                                const text = sanitizeQty(event.target.value, allowFraction);
                                const qty = parseQty(text);

                                setQtyText(qty === MAX_QTY ? formatQty(MAX_QTY) : text);
                                onChange({ qty });
                            }}
                            onBlur={() => setQtyText(formatQty(draft.qty))}
                        />
                    </div>

                    <div>
                        <label className={styles.label}>Ед. изм.</label>
                        <Select
                            options={unitOptions}
                            value={draft.unit}
                            onChange={(unit) =>
                                onChange(unit === 'шт' ? { unit, qty: Math.round(draft.qty) } : { unit })
                            }
                            aria-label="Единица измерения"
                        />
                    </div>

                    <div>
                        <label className={styles.label}>Регион</label>
                        <Select
                            className={cx(!draft.region && styles.inherit)}
                            options={regionOptions}
                            value={draft.region || projectRegion}
                            onChange={(region) => onChange({ region })}
                            aria-label="Регион поиска для позиции"
                        />
                    </div>

                    <div>
                        <label className={styles.label}>Цена за ед., ₽</label>
                        <input
                            ref={priceRef}
                            className={styles.input}
                            placeholder="—"
                            inputMode="decimal"
                            value={priceText}
                            onChange={(event) => {
                                const before = countBeforeCaret(event.target);
                                const text = groupPrice(sanitizeRequestPriceInput(stripSpaces(event.target.value)));

                                priceCaret.current = caretAfter(text, before);
                                setPriceText(text);
                                onChange({ requestPrice: parseRequestPrice(stripSpaces(text)) });
                            }}
                            onBlur={() => setPriceText(groupPrice(formatRequestPrice(draft.requestPrice)))}
                        />
                    </div>

                    <div>
                        <label className={styles.label}>Цена от, ₽</label>

                        <input
                            className={styles.input}
                            placeholder="—"
                            inputMode="numeric"
                            key={`from-${draft.priceFrom}`}
                            defaultValue={groupInt(draft.priceFrom)}
                            onChange={(event) => {
                                const input = event.target;
                                const before = countBeforeCaret(input);

                                input.value = groupInt(digitsOf(input.value));
                                restoreCaret(input, before);
                            }}
                            onBlur={(event) => {
                                const next = digitsOf(event.target.value);
                                if (next !== draft.priceFrom) onChange({ priceFrom: next });
                            }}
                        />
                    </div>

                    <div>
                        <label className={styles.label}>Цена до, ₽</label>

                        <input
                            className={styles.input}
                            placeholder="—"
                            inputMode="numeric"
                            key={`to-${draft.priceTo}`}
                            defaultValue={groupInt(draft.priceTo)}
                            onChange={(event) => {
                                const input = event.target;
                                const before = countBeforeCaret(input);

                                input.value = groupInt(digitsOf(input.value));
                                restoreCaret(input, before);
                            }}
                            onBlur={(event) => {
                                const next = digitsOf(event.target.value);
                                if (next !== draft.priceTo) onChange({ priceTo: next });
                            }}
                        />
                    </div>
                </div>

                {showDimensions && draft.logistics && (
                    <div className={styles.showDimensions}>
                        <p className={styles.dimsTitle}>Габариты товара (эталон)</p>

                        <div className={styles.dims}>
                            <div>
                                <label className={styles.label}>Длина, мм</label>
                                <input
                                    className={styles.input}
                                    placeholder="—"
                                    inputMode="numeric"
                                    value={draft.length || ''}
                                    onChange={(event) => onChange({ length: Number(event.target.value) || undefined })}
                                />
                            </div>

                            <div>
                                <label className={styles.label}>Ширина, мм</label>
                                <input
                                    className={styles.input}
                                    placeholder="—"
                                    inputMode="numeric"
                                    value={draft.width || ''}
                                    onChange={(event) => onChange({ width: Number(event.target.value) || undefined })}
                                />
                            </div>

                            <div>
                                <label className={styles.label}>Высота, мм</label>
                                <input
                                    className={styles.input}
                                    placeholder="—"
                                    inputMode="numeric"
                                    value={draft.height || ''}
                                    onChange={(event) => onChange({ height: Number(event.target.value) || undefined })}
                                />
                            </div>

                            <div>
                                <label className={styles.label}>Вес, г</label>
                                <input
                                    className={styles.input}
                                    placeholder="—"
                                    inputMode="decimal"
                                    value={draft.weight || ''}
                                    onChange={(event) =>
                                        onChange({
                                            weight: Number(event.target.value.replace(',', '.')) || undefined,
                                        })
                                    }
                                />
                            </div>
                        </div>

                        <p className={styles.dimsTitle}>Транспортные габариты</p>
                        <p className={styles.dimsHint}>
                            Размеры места с упаковкой. Оставьте пустыми, если товар едет
                            в заводской упаковке как есть — тогда доставка считается по
                            габаритам товара.
                        </p>

                        <div className={styles.dims}>
                            <div>
                                <label className={styles.label}>Длина, мм</label>
                                <input
                                    className={styles.input}
                                    placeholder="—"
                                    inputMode="numeric"
                                    value={draft.transportLength || ''}
                                    onChange={(event) =>
                                        onChange({
                                            transportLength:
                                                Number(event.target.value) || undefined,
                                        })
                                    }
                                />
                            </div>

                            <div>
                                <label className={styles.label}>Ширина, мм</label>
                                <input
                                    className={styles.input}
                                    placeholder="—"
                                    inputMode="numeric"
                                    value={draft.transportWidth || ''}
                                    onChange={(event) =>
                                        onChange({
                                            transportWidth:
                                                Number(event.target.value) || undefined,
                                        })
                                    }
                                />
                            </div>

                            <div>
                                <label className={styles.label}>Высота, мм</label>
                                <input
                                    className={styles.input}
                                    placeholder="—"
                                    inputMode="numeric"
                                    value={draft.transportHeight || ''}
                                    onChange={(event) =>
                                        onChange({
                                            transportHeight:
                                                Number(event.target.value) || undefined,
                                        })
                                    }
                                />
                            </div>

                            <div>
                                <label className={styles.label}>Вес, г</label>
                                <input
                                    className={styles.input}
                                    placeholder="—"
                                    inputMode="numeric"
                                    value={draft.transportWeight || ''}
                                    onChange={(event) =>
                                        onChange({
                                            transportWeight:
                                                Number(
                                                    event.target.value.replace(',', '.'),
                                                ) || undefined,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.switches}>
                    <div className={styles.option}>
                        <Switch
                            label="Искать аналоги"
                            discription="похожие товары других марок"
                            checked={draft.analogs}
                            onChange={(analogs) => onChange({ analogs })}
                        />
                    </div>

                    <div className={styles.option}>
                        <Switch
                            label="Считать логистику"
                            discription="доставка до адреса проекта"
                            checked={draft.logistics}
                            onChange={(logistics) => onChange({ logistics })}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};